import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Find in Store — geocodes the entered location and re-sorts the boutique list
 * nearest-first. Each boutique <li> carries data-lat / data-lng (from the boutique
 * metaobject). Geocoding sits behind a small provider adapter: Nominatim
 * (OpenStreetMap, keyless, DEV ONLY — no SLA and its usage policy requires visible
 * attribution) by default, or Google's Maps JS Geocoder when a key is set on the
 * theme setting (data-places-key). The Maps JS API (not the Geocoding web service)
 * is used deliberately: web-service endpoints reject referrer-restricted keys, and
 * a key rendered into the DOM must be HTTP-referrer-restricted to the store's
 * domains (+ API-restricted to Maps JavaScript API / Geocoding API).
 *
 * Variant changes: the drawer's Liquid renders for `selected_or_first_available_variant`
 * (size line, price, media, per-boutique store_availabilities), so it listens for the
 * variant picker's product-select event on its section — same pattern as
 * product-price.js — and swaps the product summary + boutique list from the freshly
 * fetched document, re-applying the user's nearest-first sort.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} input - The location text input.
 * @property {HTMLElement} list - The boutique list container.
 * @property {HTMLElement} [message] - Status/error line.
 * @property {HTMLElement} [suggestions] - Typeahead listbox (PJ-4817).
 * @extends {Component<Refs>}
 */
/* Country restriction: all boutiques are Australian, and the typical query is a
   bare AU suburb or postcode ("2021" should resolve to Paddington NSW, not another
   country's 2021). Google needs this as a componentRestrictions HARD filter — the
   `region` param is only a bias and returns ZERO_RESULTS for a bare postcode.
   Revisit if international boutiques are ever added. */
const GEOCODE_REGION = 'AU';

class FindInStore extends Component {
  requiredRefs = ['input'];

  /** @type {Promise<boolean> | null} Maps JS API loader (in flight or settled). */
  #mapsLoader = null;

  /** @type {object | null} Reused google.maps.Geocoder instance. */
  #geocoder = null;

  /** @type {{lat: number, lng: number} | null} Last search origin, re-applied after variant swaps. */
  #origin = null;

  /** @type {object | null} Places AutocompleteSessionToken — one per typing
   * session, reset after a pick so Google bills prediction+detail as a
   * session (PJ-4817). */
  #autocomplete = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} Typeahead debounce. */
  #suggestTimer = undefined;

  /** @type {number} Discards out-of-order prediction responses. */
  #suggestSeq = 0;

  /** @type {number} Keyboard-highlighted suggestion (-1 = none). */
  #activeIndex = -1;

  /** @type {Element | null} Section the product-select listener is bound to. */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    // Not `closest('.shopify-section, dialog')` (product-price.js) — that would stop
    // at this drawer's own dialog, which the variant picker's event never reaches.
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  // (PJ-4814's in-drawer size row — selectSize proxy + size-system mirror —
  // was removed 2026-09-02: the PDP trigger now gates on a selected size, so
  // the drawer always renders the selected variant's availability.)

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section = null;
  }

  /**
   * A new variant was selected — swap the variant-dependent pieces from the
   * re-rendered document the variant picker fetched.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const fresh = detail?.html?.querySelector('find-in-store');
        if (!fresh) return;

        const summary = this.querySelector('.find-in-store__product');
        const freshSummary = fresh.querySelector('.find-in-store__product');
        if (summary && freshSummary) summary.replaceWith(freshSummary);

        // Swap the whole results container so the boutique list always
        // reflects the live variant's per-store availability.
        const results = this.querySelector('.find-in-store__results');
        const freshResults = fresh.querySelector('.find-in-store__results');
        if (results && freshResults) {
          results.replaceWith(freshResults);
          // Keep the user's nearest-first ordering across the swap.
          const freshList = freshResults.querySelector('[ref="list"]');
          if (this.#origin && freshList) this.#sortByDistance(this.#origin, freshList);
        }
      })
      .catch(() => {
        // Aborted/failed variant fetches are handled by the picker; keep current content.
      });
  };

  /**
   * Submit handler (bound via on:submit="/search"): geocode the query, then sort.
   * @param {SubmitEvent} [event]
   */
  async search(event) {
    event?.preventDefault();

    const query = this.refs.input.value.trim();
    if (!query) return;

    this.#setMessage('');
    this.classList.add('find-in-store--busy');

    try {
      const origin = await this.#geocode(query);
      if (!origin) {
        this.#setMessage(this.dataset.noResults || '');
        return;
      }
      this.#origin = origin;
      this.#sortByDistance(origin);
    } catch (error) {
      this.#setMessage(this.dataset.noResults || '');
    } finally {
      this.classList.remove('find-in-store--busy');
    }
  }

  /**
   * PJ-4817 (#RGR-50): typeahead — debounce the input, then ask Google Places
   * for AU suburb/postcode predictions. Bound via on:input. No key (or the
   * Places API missing from it) degrades to plain manual entry — Nominatim is
   * NOT used for suggestions: its usage policy forbids per-keystroke bursts.
   * @param {InputEvent} event
   */
  suggest(event) {
    clearTimeout(this.#suggestTimer);
    const query = this.refs.input.value.trim();
    if (!this.dataset.placesKey?.trim() || query.length < 3) {
      this.#closeSuggestions();
      return;
    }
    this.#suggestTimer = setTimeout(() => this.#fetchSuggestions(query), 250);
  }

  /**
   * Keyboard support for the suggestion listbox (combobox pattern): arrows
   * move the highlight, Enter picks it (falling through to the normal submit
   * when nothing is highlighted), Escape closes. Bound via on:keydown.
   * @param {KeyboardEvent} event
   */
  suggestKeydown(event) {
    const list = this.refs.suggestions;
    if (!list || list.hidden) return;
    const options = /** @type {HTMLElement[]} */ (Array.from(list.querySelectorAll('.find-in-store__suggestion')));
    if (options.length === 0) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.#activeIndex = (this.#activeIndex + step + options.length) % options.length;
      options.forEach((option, index) => {
        const active = index === this.#activeIndex;
        option.classList.toggle('find-in-store__suggestion--active', active);
        option.setAttribute('aria-selected', String(active));
      });
      const active = options[this.#activeIndex];
      if (active) this.refs.input.setAttribute('aria-activedescendant', active.id);
    } else if (event.key === 'Enter' && this.#activeIndex >= 0) {
      event.preventDefault();
      const active = options[this.#activeIndex];
      if (active) this.#selectPrediction(active);
    } else if (event.key === 'Escape') {
      this.#closeSuggestions();
    }
  }

  /**
   * Pointer pick (pointerdown beats the input's blur). Bound via
   * on:pointerdown on the listbox; delegated dispatch, so resolve the row
   * from event.target (currentTarget is the document — component.js).
   * @param {PointerEvent} event
   */
  pickSuggestion(event) {
    const row = event.target instanceof Element ? event.target.closest('.find-in-store__suggestion') : null;
    if (!(row instanceof HTMLElement)) return;
    event.preventDefault();
    this.#selectPrediction(row);
  }

  /** Close the listbox shortly after focus leaves the input (bound via on:blur). */
  closeSuggestionsSoon() {
    setTimeout(() => this.#closeSuggestions(), 150);
  }

  /**
   * Fetch suggestions via the CURRENT Places API (AutocompleteSuggestion,
   * "Places API (New)") — the legacy AutocompleteService is not available to
   * new Google Cloud projects (REQUEST_DENIED, verified on this key).
   * Requires "Places API (New)" enabled on the key.
   * @param {string} query
   */
  async #fetchSuggestions(query) {
    const key = this.dataset.placesKey?.trim();
    if (!key) return;
    const loaded = await this.#loadMapsApi(key);
    // @ts-ignore
    if (!loaded || typeof window.google?.maps?.importLibrary !== 'function') return;

    const seq = ++this.#suggestSeq;
    try {
      // @ts-ignore
      const { AutocompleteSuggestion, AutocompleteSessionToken } = await window.google.maps.importLibrary('places');
      this.#autocomplete ??= new AutocompleteSessionToken();

      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        includedRegionCodes: [GEOCODE_REGION.toLowerCase()],
        // Suburbs + postcodes, not street addresses — the field's job is
        // "find your nearest boutique", not full address entry.
        includedPrimaryTypes: ['locality', 'postal_code'],
        sessionToken: this.#autocomplete,
      });

      if (seq !== this.#suggestSeq) return;
      const rows = (suggestions || [])
        .map((/** @type {any} */ s) => s.placePrediction)
        .filter(Boolean)
        .slice(0, 5)
        .map((/** @type {any} */ p) => ({
          placeId: p.placeId || '',
          description: p.text?.text || '',
          main: p.mainText?.text || p.text?.text || '',
          secondary: p.secondaryText?.text || '',
        }));

      if (rows.length === 0) {
        this.#closeSuggestions();
        return;
      }
      this.#renderSuggestions(rows);
    } catch (error) {
      // Places API (New) not enabled on the key, quota, or network — degrade
      // silently to manual entry (the field still geocodes on submit).
      if (seq === this.#suggestSeq) this.#closeSuggestions();
    }
  }

  /** @param {{placeId: string, description: string, main: string, secondary: string}[]} rows */
  #renderSuggestions(rows) {
    const list = this.refs.suggestions;
    if (!list) return;

    list.textContent = '';
    rows.forEach((row, index) => {
      const item = document.createElement('li');
      item.setAttribute('role', 'presentation');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'find-in-store__suggestion';
      button.id = `find-in-store-suggestion-${index}`;
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', 'false');
      button.dataset.placeId = row.placeId;
      button.dataset.description = row.description;
      // tabindex -1: focus stays on the input (combobox pattern).
      button.tabIndex = -1;

      const main = document.createElement('span');
      main.className = 'find-in-store__suggestion-main';
      main.textContent = row.main;
      button.appendChild(main);

      if (row.secondary) {
        const detail = document.createElement('span');
        detail.className = 'find-in-store__suggestion-secondary';
        detail.textContent = row.secondary;
        button.appendChild(detail);
      }

      item.appendChild(button);
      list.appendChild(item);
    });

    this.#activeIndex = -1;
    list.hidden = false;
    this.refs.input.setAttribute('aria-expanded', 'true');
    this.refs.input.removeAttribute('aria-activedescendant');
  }

  #closeSuggestions() {
    const list = this.refs.suggestions;
    if (list) {
      list.hidden = true;
      list.textContent = '';
    }
    this.#activeIndex = -1;
    this.refs.input.setAttribute('aria-expanded', 'false');
    this.refs.input.removeAttribute('aria-activedescendant');
  }

  /** @param {HTMLElement} row */
  #selectPrediction(row) {
    const description = row.dataset.description || row.textContent?.trim() || '';
    this.refs.input.value = description;
    this.#closeSuggestions();
    // A pick ends the Places billing session — next typing starts a new one.
    this.#autocomplete = null;
    this.#searchPrediction(row.dataset.placeId || '', description);
  }

  /**
   * Resolve a picked prediction to coordinates (place ID first — exact —
   * falling back to a plain geocode of its text) and sort the boutiques.
   * @param {string} placeId
   * @param {string} description
   */
  async #searchPrediction(placeId, description) {
    this.#setMessage('');
    this.classList.add('find-in-store--busy');

    try {
      let origin = null;

      if (placeId) {
        const key = this.dataset.placesKey?.trim();
        if (key && (await this.#loadMapsApi(key))) {
          // @ts-ignore
          this.#geocoder ??= new window.google.maps.Geocoder();
          try {
            const { results } = await this.#geocoder.geocode({ placeId });
            const location = results?.[0]?.geometry?.location;
            if (location) origin = { lat: location.lat(), lng: location.lng() };
          } catch (error) {
            // Fall through to the text geocode below.
          }
        }
      }

      origin ??= await this.#geocode(description);
      if (!origin) {
        this.#setMessage(this.dataset.noResults || '');
        return;
      }
      this.#origin = origin;
      this.#sortByDistance(origin);
    } catch (error) {
      this.#setMessage(this.dataset.noResults || '');
    } finally {
      this.classList.remove('find-in-store--busy');
    }
  }

  /**
   * @param {string} query
   * @returns {Promise<{lat: number, lng: number} | null>}
   */
  async #geocode(query) {
    const key = this.dataset.placesKey?.trim();
    if (key) return this.#geocodeGoogle(query, key);
    return this.#geocodeNominatim(query);
  }

  /** Keyless DEV-ONLY geocoder (OpenStreetMap). Rate-limited; attribution required. */
  async #geocodeNominatim(query) {
    const url = new URL('https://nominatim.openstreetmap.org/search');
    url.searchParams.set('q', query);
    url.searchParams.set('format', 'json');
    url.searchParams.set('limit', '1');
    url.searchParams.set('countrycodes', GEOCODE_REGION.toLowerCase());

    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;

    const data = await response.json();
    if (!Array.isArray(data) || data.length === 0) return null;

    const lat = parseFloat(data[0].lat);
    const lng = parseFloat(data[0].lon);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }

  /**
   * Production geocoder — used automatically once a Google Maps key is set.
   * Runs through the Maps JS API's Geocoder so the key can be referrer-restricted
   * (the Geocoding web service rejects referrer-restricted keys).
   * @param {string} query
   * @param {string} key
   * @returns {Promise<{lat: number, lng: number} | null>}
   */
  async #geocodeGoogle(query, key) {
    const loaded = await this.#loadMapsApi(key);
    if (!loaded) return null;

    // @ts-ignore — google.maps comes from the injected Maps JS script.
    this.#geocoder ??= new window.google.maps.Geocoder();

    try {
      const { results } = await this.#geocoder.geocode({
        address: query,
        componentRestrictions: { country: GEOCODE_REGION },
      });
      const location = results?.[0]?.geometry?.location;
      if (!location) return null;

      const lat = location.lat();
      const lng = location.lng();
      return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
    } catch (error) {
      // The Geocoder promise rejects on ZERO_RESULTS — treat as "not found".
      return null;
    }
  }

  /**
   * Inject the Maps JS API once, on first search only (no PDP load-time cost).
   * @param {string} key
   * @returns {Promise<boolean>} true when google.maps.Geocoder is available.
   */
  #loadMapsApi(key) {
    // @ts-ignore
    if (window.google?.maps?.Geocoder) return Promise.resolve(true);

    this.#mapsLoader ??= new Promise((resolve) => {
      const callbackName = '__fisMapsCallback';
      // @ts-ignore
      window[callbackName] = () => {
        // @ts-ignore
        delete window[callbackName];
        // @ts-ignore
        resolve(Boolean(window.google?.maps?.Geocoder));
      };

      const url = new URL('https://maps.googleapis.com/maps/api/js');
      url.searchParams.set('key', key);
      url.searchParams.set('v', 'weekly');
      url.searchParams.set('loading', 'async');
      // PJ-4817 (#RGR-50): places powers the typeahead suggestions. The key
      // must have the Places API enabled alongside Maps JS/Geocoding —
      // suggestions degrade to plain manual entry if it doesn't.
      url.searchParams.set('libraries', 'places');
      url.searchParams.set('callback', callbackName);

      const script = document.createElement('script');
      script.src = url.toString();
      script.async = true;
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    });

    return this.#mapsLoader;
  }

  /**
   * Re-order the list nearest-first. Items without coordinates sink to the bottom.
   * @param {{lat: number, lng: number}} origin
   * @param {Element | null} [list] - Defaults to refs.list; pass the element directly
   *   after a node swap (refs update async via MutationObserver).
   */
  #sortByDistance(origin, list = this.refs.list) {
    if (!list) return;
    const items = /** @type {HTMLElement[]} */ (Array.from(list.querySelectorAll('.find-in-store__item')));
    const ranked = items
      .map((el) => {
        const lat = parseFloat(el.dataset.lat ?? '');
        const lng = parseFloat(el.dataset.lng ?? '');
        const distance = Number.isFinite(lat) && Number.isFinite(lng) ? haversine(origin, { lat, lng }) : Infinity;
        return { el, distance };
      })
      .sort((a, b) => a.distance - b.distance);

    // Re-append in the new order (a no-op move for already-correct positions).
    for (const { el } of ranked) list.appendChild(el);
  }

  /** @param {string} text */
  #setMessage(text) {
    const message = this.refs.message;
    if (!message) return;
    message.textContent = text;
    message.hidden = !text;
  }
}

/**
 * Great-circle distance in km between two {lat, lng} points.
 * @param {{lat: number, lng: number}} a
 * @param {{lat: number, lng: number}} b
 * @returns {number}
 */
function haversine(a, b) {
  const R = 6371;
  const toRad = (/** @type {number} */ deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
}

if (!customElements.get('find-in-store')) {
  customElements.define('find-in-store', FindInStore);
}
